import { useHistory, useParams } from "react-router";
import { Form, Toggle, Panel } from "rsuite";
import { Button } from "../../components/button.component";
import {
  TextInput,
  TagInputWithIcon,
  FileInput,
  DateInput,
  NumberInput,
} from "../../components/textInput.component";
import { AppRouteApi, AppRouteUi } from "../../config/appRoute";
import imageUploadLogo from "../../assets/img/Upload-Photo-patient.svg";
import fileUploadLogo from "../../assets/img/UploadCertificate.svg";
import { useState, useEffect, useCallback } from "react";
import {
  TextAreaInput,
  SelectPickerInput,
} from "../../components/textInput.component";
import { ApiPatient } from "../../core/apis/patient";
import { PatientFormSchema } from "../../models/forms/patientForm";
import {
  DoesObjectHasAnyInvalidValue,
  generateMultipartFormData,
} from "../../utils/formUtils";
import { UploadedFileType } from "../../models/forms/uploadFileType";
import { UtilityDate } from "../../utils/dateUtils";
import { useAppDispatch } from "../../core/redux/ReduxStore";
import { ActionsPatientTable } from "../../core/redux/slices/slicePatientTable";
import { StorageAuth } from "../../core/localStorage";
import { Language } from "../../utils/language";
import { SpinLoader } from "../../components/spinLoader.component";
import { LocationSearchInput } from "../../components/locationSearchInput.component";
import { AddTaskSchema } from "../../models/api/patient";
import { AddTaskOnPatient } from "../../components/addTaskOnPatient.component";
import { DHS_INSURANCE_NAME } from "../../config/constants";
import { TimeUtility } from "../../utils";
import {
  EnumInvoiceGenerationInvokeType,
  generateDropdownListForEnumInvoiceGenerationInvokeType,
} from "../../libs/enums/EnumPatientInvoketype";

type PatientFormData = {
  CaseWorker: string;
  CaseWorkerPhoneNumber?: string;
  CaseWorkerEmail: string;
  CaseManager?: string;
  CaseManagerPhoneNumber?: string;
  CaseManagerEmail?: string;
  DateOfBirth?: Date;
  FirstName?: string;
  FormAttachFile?: UploadedFileType[];
  Gender?: string;
  HHAExchangeId?: string;
  InvoiceGenerationInvokeType?: string;
  InsuranceId?: string;
  InsuranceTypeId?: string;
  InsuranceTypeName?: string;
  County?: string;
  LastName?: string;
  MedicalCode?: string;
  PhoneNumber?: string;
  ProfilePic?: UploadedFileType[];
  PerUnitCost?: number;
  ServiceAuthorizationEndDate?: Date;
  ServiceAuthorizationFile?: UploadedFileType[];
  ServiceAuthorizationStartDate?: Date;
  SpecialInstructions?: string;
  TaskSKeys?: string[];
  TotalUnit?: string;
  Email?: string;
  Address?: string;
  Languages?: string[];
  EmergencyContactPersonName?: string;
  EmergencyContactPersonPhone?: string;
  ApartmentNumber?: string;
  AgencyOwnerSignature?: UploadedFileType[];
};

export const UpdatePatient = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const { data, isValidating } = ApiPatient.ById.Hook(patientId);
  const routeHistory = useHistory();
  const [formError, setFormError] = useState({});
  const { data: insuranceTypes } = ApiPatient.GetAllInsuranceTypes.Hook(
    StorageAuth.CompanyId!
  );
  const [isAddressTouched, setIsAddressTouched] = useState(false);
  const [tasks, setTasks] = useState<AddTaskSchema[]>([]);
  const [inputTime, setInputTime] = useState(0);
  const [validationSummary, setValidationSummary] = useState<string[]>([]);

  const genders = ["Male", "Female", "Other"].map((gender) => ({
    label: gender,
    value: gender,
  }));

  const invoiceGenerationInvokeTypes: {
    label: string;
    value: EnumInvoiceGenerationInvokeType;
  }[] = generateDropdownListForEnumInvoiceGenerationInvokeType();

  const insuranceTypeMap =
    insuranceTypes?.map((it) => ({
      label: it.displayName ?? it.name,
      value: it.sKey,
    })) ?? [];

  const getCounty = (insTypeId: string) => {
    var filteredInsuranceType = insuranceTypes?.find(
      (t) => t.sKey === insTypeId
    );
    if (filteredInsuranceType) {
      return (
        filteredInsuranceType.insuranceCounty?.map((ic) => ({
          label: ic,
          value: ic,
        })) ?? []
      );
    }
    return [];
  };
  const [isTransportAvailable, setIsTransportAvailable] = useState(false);
  const [isSignatureRequired, setIsSignatureRequired] = useState(false);
  const [isCostMeasureTypeHourly, setIsCostMeasureTypeHourly] = useState(false);
  const [formData, setFormData] = useState<PatientFormData>(
    {} as PatientFormData
  );

  useEffect(() => {
    if (!data) {
      return;
    }
    setFormData({
      CaseWorker: data.caseWorker,
      CaseWorkerPhoneNumber: data.caseWorkerPhoneNumber,
      CaseWorkerEmail: data.caseWorkerEmail,
      CaseManager: data?.caseManager,
      CaseManagerPhoneNumber: data?.caseManagerPhoneNumber,
      CaseManagerEmail: data?.caseManagerEmail,
      DateOfBirth: new Date(data?.dateOfBirth || Date()),
      FirstName: data?.firstName,
      Gender: data?.gender,
      HHAExchangeId: data?.hhaExchangeId || "",
      InvoiceGenerationInvokeType: data?.invoiceGenerationInvokeType,
      InsuranceId: data?.insuranceId,
      InsuranceTypeId: data?.insuranceTypeId,
      InsuranceTypeName: data?.insuranceTypeName,
      County: data?.county,
      LastName: data?.lastName,
      MedicalCode: data?.medicalCode,
      PhoneNumber: data?.phoneNumber,
      PerUnitCost: data?.perUnitCost,
      ServiceAuthorizationStartDate: new Date(
        data?.serviceAuthorizationStartDate || Date()
      ),
      ServiceAuthorizationEndDate: new Date(
        data?.serviceAuthorizationEndDate || Date()
      ),
      SpecialInstructions: data?.specialInstructions,
      TotalUnit: `${data?.totalUnit}`,
      Email: data?.email,
      Address: data?.address,
      Languages: data?.languages,
      EmergencyContactPersonName: data?.emergencyContactPersonName,
      EmergencyContactPersonPhone: data?.emergencyContactPersonPhone,
      ApartmentNumber: data?.apartmentNumber,
    });
    setIsTransportAvailable(!!data?.isTransportAvailable);
    setIsSignatureRequired(!!data?.isSignatureRequired);
    setIsCostMeasureTypeHourly(!!data?.isCostMeasureTypeHourly);
    setTasks(data?.taskDetailsList ?? []);
    convertToPhoneNumberFormat(
      data?.phoneNumber ? data?.phoneNumber : "",
      "PhoneNumber"
    );
    convertToPhoneNumberFormat(
      data?.caseManagerPhoneNumber ? data?.caseManagerPhoneNumber : "",
      "CaseManagerPhoneNumber"
    );
    convertToPhoneNumberFormat(
      data?.emergencyContactPersonPhone
        ? data?.emergencyContactPersonPhone
        : "",
      "EmergencyContactPersonPhone"
    );
  }, [data]);

  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const {
      SpecialInstructions,
      ApartmentNumber,
      County,
      Email,
      CaseManager,
      CaseManagerEmail,
      CaseManagerPhoneNumber,
      CaseWorkerPhoneNumber,
      HHAExchangeId, // Exclude this from validation
      ...rest
    } = formData;
    const isEmpty = Object.values({ ...rest }).some(
      DoesObjectHasAnyInvalidValue
    );
    const errorCount = Object.keys(formError).length;
    const taskValidated = (() => {
      if (tasks.length) {
        return tasks.every((task) => {
          let haveEmptyTask = false;
          if (task.isMonthly) {
            haveEmptyTask = !task.totalTaskPerMonth;
          } else {
            haveEmptyTask = !task.weeklyAvailabilities?.length;
          }
          return task.taskSkey && !haveEmptyTask && !!task.secondsPerDay;
        });
      } else {
        return true;
      }
    })();
    const requiredFilled =
      !!formData?.ServiceAuthorizationStartDate &&
      !!formData?.DateOfBirth &&
      !!formData.ServiceAuthorizationEndDate;

    const inputTimeInHour = TimeUtility.hourFormatter(inputTime, {
      valueType: "seconds",
      fractionDigits: 2,
    });
    const totalUnitInHour = isCostMeasureTypeHourly
      ? formData.TotalUnit
      : TimeUtility.hourFormatter(Number(formData.TotalUnit), {
          valueType: "units",
          fractionDigits: 2,
        });
    var isDisabled =
      isEmpty ||
      !!errorCount ||
      !requiredFilled ||
      !taskValidated ||
      inputTimeInHour !== totalUnitInHour;
    setIsDisabled(isDisabled);

    // Generate validation summary
    const errors: string[] = [];

    // Required fields check
    if (isEmpty) {
      errors.push("Fill in all required fields");
    }

    // Form validation errors
    if (errorCount > 0) {
      errors.push("Fix validation errors");
    }

    // Check dates
    if (!formData.DateOfBirth) {
      errors.push("Date of Birth is required");
    }
    if (!formData.ServiceAuthorizationStartDate) {
      errors.push("Service Authorization Start Date is required");
    }
    if (!formData.ServiceAuthorizationEndDate) {
      errors.push("Service Authorization End Date is required");
    }

    // Tasks validation
    if (!taskValidated && tasks.length > 0) {
      errors.push("Complete all task details");
    }

    // Time mismatch
    if (inputTimeInHour !== totalUnitInHour && formData.TotalUnit) {
      errors.push(
        `Total ${
          isCostMeasureTypeHourly ? "hours" : "units"
        } (${totalUnitInHour}) don't match task time (${inputTimeInHour})`
      );
    }

    // Update validation summary
    setValidationSummary(errors);
  }, [formData, formError, inputTime, tasks, isCostMeasureTypeHourly]);

  const handleChange = useCallback(
    (data: any, _: any) =>
      setFormData((prevFormData) => ({ ...prevFormData, ...data })),
    []
  );

  const convertToPhoneNumberFormat = (
    phoneNumber: string,
    propertyName: string
  ) => {
    phoneNumber = phoneNumber.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;

    if (phoneNumberLength > 3 && phoneNumberLength < 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumberLength >= 7) {
      phoneNumber = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6, 10)}`;
    }

    setFormData((prev) => ({ ...prev, [propertyName]: phoneNumber }));
  };

  const handleSubmit = async (
    checked: boolean,
    event: React.FormEvent<HTMLFormElement>
  ) => {
    if (!checked) return;
    setIsLoading(true);
    const { CompanyId } = StorageAuth;
    const multipartFormData = generateMultipartFormData(formData);

    multipartFormData.append("SKey", patientId);
    multipartFormData.append("CompanyId", CompanyId || data?.companyId || "");

    multipartFormData.append(
      "IsTransportAvailable",
      isTransportAvailable ? "true" : "false"
    );

    multipartFormData.append(
      "IsSignatureRequired",
      isSignatureRequired ? "true" : "false"
    );

    multipartFormData.append(
      "IsCostMeasureTypeHourly",
      isCostMeasureTypeHourly ? "true" : "false"
    );

    tasks.forEach((task, index) => {
      multipartFormData.append(
        `TaskDetailsList[${index}].taskSkey`,
        task.taskSkey!
      );
      multipartFormData.append(
        `TaskDetailsList[${index}].isMonthly`,
        `${task.isMonthly}`
      );
      multipartFormData.append(
        `TaskDetailsList[${index}].hasManualTimeChange`,
        `${task.hasManualTimeChange}`
      );
      multipartFormData.append(
        `TaskDetailsList[${index}].secondsPerDay`,
        `${parseInt(task.secondsPerDay!.toString())}`
      );
      if (task.isMonthly) {
        multipartFormData.append(
          `TaskDetailsList[${index}].totalTaskPerMonth`,
          `${task.totalTaskPerMonth}`
        );
      } else {
        multipartFormData.append(
          `TaskDetailsList[${index}].daysPerWeek`,
          `${task.daysPerWeek}`
        );
        task.weeklyAvailabilities?.forEach((weeklyAvailability, idx) => {
          multipartFormData.append(
            `TaskDetailsList[${index}].weeklyAvailabilities[${idx}]`,
            weeklyAvailability.toString()
          );
        });
      }
    });
    removeServiceAuthFiles.forEach((doc) =>
      multipartFormData.append("RemoveServiceAuthorizationFile", doc)
    );

    removeFormArrachFiles.forEach((doc) =>
      multipartFormData.append("RemoveFormAttachFile", doc)
    );

    const result = await ApiPatient.Update(multipartFormData);

    if (result.isSuccess) {
      dispatch(ActionsPatientTable.goToPage(1));
      routeHistory.length > 1
        ? routeHistory.goBack()
        : routeHistory.push(AppRouteUi.Root);
    }
    setIsLoading(false);
  };

  const disabledInsuranceType = () => {
    const disabledIT: string[] = [];
    insuranceTypes?.forEach((it, idx) => {
      if (it.isDisabled) disabledIT.push(insuranceTypeMap[idx].value!);
    });
    return disabledIT;
  };

  const [removeServiceAuthFiles, setRemoveServiceAuthFiles] = useState<
    string[]
  >([]);
  const [removeFormArrachFiles, setRemoveFormArrachFiles] = useState<string[]>(
    []
  );

  const handleServiceAuthFileClick = (doc: string) => {
    removeServiceAuthFiles.includes(doc)
      ? setRemoveServiceAuthFiles(
          removeServiceAuthFiles.filter((existing) => existing !== doc)
        )
      : setRemoveServiceAuthFiles([...removeServiceAuthFiles, doc]);
  };

  const handleFormAttachFileClick = (doc: string) => {
    removeFormArrachFiles.includes(doc)
      ? setRemoveFormArrachFiles(
          removeFormArrachFiles.filter((existing) => existing !== doc)
        )
      : setRemoveFormArrachFiles([...removeFormArrachFiles, doc]);
  };

  return (
    <>
      {isValidating ? (
        <SpinLoader />
      ) : (
        <Form
          className="row"
          onChange={handleChange}
          onSubmit={handleSubmit}
          formValue={formData}
          onCheck={setFormError}
          model={PatientFormSchema}
          fluid
        >
          <div className="col-md-10 col-12 border-end px-4 add-page-ctl add-page-ctl">
            <div className="row">
              <TextInput name="FirstName" required>
                First Name
              </TextInput>
              <TextInput name="LastName" required>
                Last Name
              </TextInput>
            </div>

            <div className="row">
              <SelectPickerInput
                data={genders}
                name="Gender"
                required
                searchable={false}
                renderEmpty={
                  <p className="text-muted p-3">No Data Available</p>
                }
              >
                Gender
              </SelectPickerInput>
              <DateInput
                name="DateOfBirth"
                ranges={[]}
                disabledDate={UtilityDate.isDateAfterToday}
                required
              >
                Date of birth
              </DateInput>
            </div>

            <div className="row">
              <TextInput
                name="PhoneNumber"
                required
                value={formData.PhoneNumber}
                onChange={(val) =>
                  convertToPhoneNumberFormat(val, "PhoneNumber")
                }
              >
                Phone Number
              </TextInput>
              <TextInput name="Email">Email</TextInput>
            </div>

            <div className="row">
              <SelectPickerInput
                required
                name="InsuranceTypeId"
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    InsuranceTypeId: e,
                    County: null!,
                  });
                }}
                data={insuranceTypeMap}
                renderEmpty={
                  <p className="text-muted p-3">No Data Available</p>
                }
                disabledItemValues={disabledInsuranceType()}
              >
                Insurance type
              </SelectPickerInput>
              <SelectPickerInput
                data={invoiceGenerationInvokeTypes}
                name="InvoiceGenerationInvokeType"
                required
                searchable={false}
                renderEmpty={
                  <p className="text-muted p-3">No Data Available</p>
                }
              >
                Invoice Generation Invoke Type
              </SelectPickerInput>
              {formData.InsuranceTypeId && (
                <FileInput
                  listType="picture"
                  imageSrc={imageUploadLogo}
                  name="AgencyOwnerSignature"
                  accept="image/*"
                  imageSrcApi={
                    data?.agencyOwnerSignature
                      ? AppRouteApi.Media.ByPath(
                          data?.agencyOwnerSignature || ""
                        )
                      : imageUploadLogo
                  }
                >
                  Authorize Representative/Agency Owner Signature
                </FileInput>
              )}
            </div>

            {!!formData.InsuranceTypeId &&
            getCounty(formData.InsuranceTypeId).length ? (
              <>
                <div className="row">
                  <SelectPickerInput
                    data={getCounty(formData.InsuranceTypeId)}
                    name="County"
                    renderEmpty={
                      <p className="text-muted p-3">No Data Available</p>
                    }
                  >
                    County
                  </SelectPickerInput>
                </div>
              </>
            ) : null}

            <div className="row">
              <Form.Group className="col-md-6 col-12 px-1">
                <Form.ControlLabel>
                  Address <span className="text-red-300">*</span>
                </Form.ControlLabel>
                <LocationSearchInput
                  onSelect={(address) => {
                    setFormData({ ...formData, Address: address });
                  }}
                  onBlur={() => setIsAddressTouched(true)}
                  value={formData.Address}
                />
                {isAddressTouched && !formData.Address && (
                  <small className="text-red-300 mb-2">
                    Please select a valid address
                  </small>
                )}
              </Form.Group>
              <TextInput name="ApartmentNumber">Apartment</TextInput>
            </div>

            <div className="row">
              <TextInput name="EmergencyContactPersonName" required>
                Emergency Contact Person Name
              </TextInput>
              <TextInput
                name="EmergencyContactPersonPhone"
                required
                value={formData.EmergencyContactPersonPhone}
                onChange={(val) =>
                  convertToPhoneNumberFormat(val, "EmergencyContactPersonPhone")
                }
              >
                Emergency Contact Person Phone
              </TextInput>
            </div>

            <div className="row">
              <TextInput name="InsuranceId" required>
                Member/insurance id number
              </TextInput>
              <TextInput name="MedicalCode" required>
                Medical code
              </TextInput>
            </div>

            <div className="row">
              <TextInput name="HHAExchangeId">HHA Exchange ID</TextInput>
            </div>

            <div className="row mb-3">
              <DateInput
                name="ServiceAuthorizationStartDate"
                disabledDate={(date?: Date) =>
                  !!date &&
                  !!formData.ServiceAuthorizationEndDate &&
                  date > formData.ServiceAuthorizationEndDate
                }
                required
              >
                Service Authorization start date
              </DateInput>
              <DateInput
                name="ServiceAuthorizationEndDate"
                disabledDate={(date?: Date) =>
                  !!date &&
                  !!formData.ServiceAuthorizationStartDate &&
                  date < formData.ServiceAuthorizationStartDate
                }
                required
              >
                Service Authorization end date
              </DateInput>
            </div>

            <div className="row">
              <div className="col-md-3 align-items-center px-1 mb-3">
                IsCostMeasureTypeHourly
                <Toggle
                  onChange={() =>
                    setIsCostMeasureTypeHourly(!isCostMeasureTypeHourly)
                  }
                  checked={isCostMeasureTypeHourly}
                  size="sm"
                  className="ms-2"
                />
              </div>
            </div>

            <div className="row">
              <NumberInput
                name="TotalUnit"
                min={0}
                required
                onChange={() => {
                  var newTasks: AddTaskSchema[] = [...tasks];
                  newTasks.forEach((eachTask) => {
                    eachTask.hasManualTimeChange = false;
                  });
                  setTasks(newTasks);
                }}
              >
                Total{" "}
                {isCostMeasureTypeHourly
                  ? "hours "
                  : "units (1 unit = 15 minutes)"}
              </NumberInput>

              <NumberInput name="PerUnitCost" required>
                Per {isCostMeasureTypeHourly ? "hour" : "unit "} cost
              </NumberInput>
            </div>

            <div className="row px-1 mb-2">
              <AddTaskOnPatient
                onChange={(tasks, iTime) => {
                  setTasks(tasks);
                  setInputTime(iTime);
                }}
                isDayCount={
                  formData.InsuranceTypeId ===
                  insuranceTypes?.find(
                    (item) =>
                      item.name.toLowerCase() ===
                      DHS_INSURANCE_NAME.toLowerCase()
                  )?.sKey
                }
                value={data?.taskDetailsList ?? []}
                totalTime={
                  isCostMeasureTypeHourly
                    ? Number(formData.TotalUnit) * 60
                    : Number(formData.TotalUnit) * 15
                }
                totalTimeInSec={
                  isCostMeasureTypeHourly
                    ? Number(formData.TotalUnit) * 60 * 60
                    : Number(formData.TotalUnit) * 15 * 60
                }
                totalUnit={Number(formData.TotalUnit)}
                perUnitCost={Number(formData.PerUnitCost) || 0}
                isCostMeasureTypeHourly={isCostMeasureTypeHourly}
              />
            </div>

            <div className="row">
              <div className="col-lg-4 p-0">
                <TextInput
                  name="CaseWorker"
                  wrapperClass="w-100"
                  required
                  value={formData.CaseWorker}
                >
                  Case Worker Name
                </TextInput>
              </div>
              <div className="col-lg-4 p-0">
                <TextInput
                  name="CaseWorkerPhoneNumber"
                  wrapperClass="w-100"
                  value={formData.CaseWorkerPhoneNumber}
                  onChange={(val) =>
                    convertToPhoneNumberFormat(val, "CaseWorkerPhoneNumber")
                  }
                >
                  Case Worker Phone Number
                </TextInput>
              </div>
              <div className="col-lg-4 p-0">
                <TextInput name="CaseWorkerEmail" wrapperClass="w-100" required>
                  Case Worker Email
                </TextInput>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-4 p-0">
                <TextInput
                  name="CaseManager"
                  wrapperClass="w-100"
                  value={formData.CaseManager}
                >
                  Case Manager Name
                </TextInput>
              </div>
              <div className="col-lg-4 p-0">
                <TextInput
                  name="CaseManagerPhoneNumber"
                  wrapperClass="w-100"
                  value={formData.CaseManagerPhoneNumber}
                  onChange={(val) =>
                    convertToPhoneNumberFormat(val, "CaseManagerPhoneNumber")
                  }
                >
                  Case Manager Phone Number
                </TextInput>
              </div>
              <div className="col-lg-4 p-0">
                <TextInput name="CaseManagerEmail" wrapperClass="w-100">
                  Case Manager Email
                </TextInput>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-4 p-0">
                <TagInputWithIcon
                  name="Languages"
                  data={Language.getLanguageSelectItems()}
                  required
                  value={formData.Languages}
                  wrapperClass="w-100"
                  renderEmpty={
                    <p className="text-muted p-3">No Data Available</p>
                  }
                >
                  Languages
                </TagInputWithIcon>
              </div>
            </div>

            <div className="row">
              <div className="col-md-3 align-items-center px-1 mb-3">
                Transportation Availability
                <Toggle
                  onChange={() =>
                    setIsTransportAvailable(!isTransportAvailable)
                  }
                  checked={isTransportAvailable}
                  size="sm"
                  className="ms-2"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-3 align-items-center px-1 mb-3">
                Signature
                <Toggle
                  onChange={() => setIsSignatureRequired(!isSignatureRequired)}
                  checked={isSignatureRequired}
                  size="sm"
                  className="ms-2"
                />
              </div>
            </div>

            <div className="row">
              <TextAreaInput name="SpecialInstructions" fullWidth>
                Special instructions
              </TextAreaInput>
            </div>

            <div className="row">
              <FileInput
                listType="picture"
                imageSrc={imageUploadLogo}
                name="ProfilePic"
                accept="image/*"
                imageSrcApi={
                  data?.profilePic
                    ? AppRouteApi.Media.ByPath(data?.profilePic || "")
                    : imageUploadLogo
                }
              >
                Profile Picture
              </FileInput>
            </div>

            <div className="row">
              <FileInput
                listType="text"
                imageSrc={fileUploadLogo}
                name="ServiceAuthorizationFile"
                multiple
              >
                Service Authorization Files
              </FileInput>
              {data?.serviceAuthorizationFile!.length ? (
                <div className="col-md-6">
                  <p>Uploaded Service Authorization Files: </p>
                  <div className="d-flex flex-column">
                    {data?.serviceAuthorizationFile.map((doc) => (
                      <div
                        key={doc}
                        className="py-1 d-flex uploadedItem align-items-center"
                        style={{
                          color: removeServiceAuthFiles.includes(doc)
                            ? "red"
                            : "black",
                        }}
                        onClick={() => {
                          handleServiceAuthFileClick(doc);
                        }}
                      >
                        <img
                          src={imageUploadLogo}
                          alt="Uploaded Item"
                          className="me-2"
                          height={35}
                        />
                        <p className="text-break w-100">
                          {doc.split("_").slice(4).join("_")}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="col-md-6 mb-2">
                  No Uploaded Service Authorization Files
                </p>
              )}
            </div>
            <div className="row">
              <FileInput
                listType="text"
                imageSrc={fileUploadLogo}
                name="FormAttachFile"
                multiple
              >
                Form Attach Files
              </FileInput>

              {data?.formAttachFile!.length ? (
                <div className="col-6">
                  <p>Uploaded Form Attach Files: </p>
                  <div className="d-flex flex-column">
                    {data?.formAttachFile.map((doc) => (
                      <div
                        key={doc}
                        className="py-1 d-flex uploadedItem align-items-center"
                        style={{
                          color: removeFormArrachFiles.includes(doc)
                            ? "red"
                            : "black",
                        }}
                        onClick={() => {
                          handleFormAttachFileClick(doc);
                        }}
                      >
                        <img
                          src={imageUploadLogo}
                          alt="Uploaded Item"
                          className="me-2"
                          height={35}
                        />
                        <p className="text-break w-100">
                          {doc.split("_").slice(4).join("_")}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="col-md-6 mb-2">No Uploaded Form Attach Files</p>
              )}
            </div>
          </div>

          <div className="col-md-2 col-12">
            <Button
              className="m-1 w-100 btn-green"
              type="submit"
              name="Save"
              loading={isLoading}
              disabled={isDisabled}
            />
            <Button
              onClick={() => {
                routeHistory.length > 1
                  ? routeHistory.goBack()
                  : routeHistory.push(AppRouteUi.Root);
              }}
              type="button"
              className="m-1 w-100 btn-green-subtle"
              disabled={isLoading}
              name="Return without saving"
            />

            {isDisabled && validationSummary.length > 0 && (
              <Panel
                header="Missing Information"
                bordered
                className="mt-3 bg-light"
              >
                <div className="text-danger small">
                  <p className="mb-2 fw-bold">Please complete:</p>
                  <ul className="ps-3 mb-0">
                    {validationSummary.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              </Panel>
            )}
          </div>
        </Form>
      )}
    </>
  );
};
